import React, { useState } from 'react';
import { Event } from '@go-shared/types/event';
import { BubbleForm } from '../BubbleUI';
import { BubbleFormPage } from '../BubbleUI/BubbleForm';
import SelectEventPage from './SelectEventPage';
import { EventTemplate } from '@go-shared/types/eventTemplate';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import ModifyEventDetailsPage, { EventFormData } from './ModifyEventDetailsPage';
import { ObjectID } from 'bson';
import { calculateTimeDifference } from '@go-shared/lib/time';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { mapBy, notUndefined } from '@go-shared/lib/functional';
import { PartialEvent } from '@go-client/lib/event';
import { BareSailingDay } from '@go-shared/types/sailing';
import { GridLocationData } from '../Grid';
import { Plan } from '@go-shared/types/plan';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import { StaffTemplate } from '@go-shared/types/staffTemplate';
import { EventTemplateTranslation } from '@go-shared/types/translation';

export interface CanvasEventFormMode {
  type: 'create' | 'edit';
  event: PartialEvent;
  initialPage?: number;
  dayTransfer?: EventFormDayTransfer;
  gridLoc?: GridLocationData;
}

export interface EventFormDayTransfer {
  current: BareSailingDay;
  options: BareSailingDay[];
}

interface CanvasEventFormProps {
  mode: CanvasEventFormMode;
  plan: Plan;
  eventTemplates: EventTemplate[];
  venueTemplates: VenueTemplate[];
  staffTemplates: StaffTemplate[];
  eventTemplateTranslations: EventTemplateTranslation[];
  onCreateTemplate?: (template: EventTemplate) => void;
  onRemoveEvent?: (eventId: string) => void;
  onClose?: () => void;
  onSubmitEvent?: (
    event: Event,
    applyStaffToPlan?: boolean,
    transferToTargetDay?: string
  ) => Promise<void>;
}

export default function CanvasEventForm(props: CanvasEventFormProps) {
  const { user } = useUserInfo();
  const { brand } = useServerInfo();
  const { activeProperty: property } = useActiveProperty();

  const { mode, plan, eventTemplates, venueTemplates, staffTemplates } = props;
  const [selectedEvtTemplate, setSelectedEvtTemplate] = useState<EventTemplate | undefined>(
    mode.event.template
  );

  async function handleSubmit(eventForm: EventFormData) {
    const { startTime, endTime } = eventForm;

    if (!selectedEvtTemplate || !startTime || !endTime || !props.onSubmitEvent) return;

    const highlightsDict = mapBy(selectedEvtTemplate.brand.eventHighlights, '_id');

    const event: Event = {
      ...eventForm,
      _id: mode.event._id ?? new ObjectID().toHexString(),
      template: selectedEvtTemplate,
      startTime,
      duration: calculateTimeDifference(endTime, startTime),
      day: plan.day,
      highlights: (eventForm.highlights || []).map(a => highlightsDict[a]).filter(notUndefined),
      callToActions: eventForm.callToActions,
      interactiveFeedDisplay:
        eventForm.interactiveFeedDisplay !== selectedEvtTemplate.interactiveFeedDisplay
          ? eventForm.interactiveFeedDisplay
          : undefined,
    };

    const applyStaffToPlan = eventForm.applyStaffToPlan;
    const transferToTargetDay = eventForm.transferDay?._id;

    return props.onSubmitEvent(event, applyStaffToPlan, transferToTargetDay);
  }

  function handleCreateTemplate(template: EventTemplate) {
    if (props.onCreateTemplate) {
      props.onCreateTemplate(template);
      setSelectedEvtTemplate(template);
    }
  }

  const pages: BubbleFormPage<unknown>[] = [];

  pages.push(
    {
      key: 'page-1',
      render: actions => (
        <SelectEventPage
          user={user}
          brand={brand}
          property={property}
          mode={mode}
          title={`${mode.type === 'create' ? 'Add' : 'Edit'} Event`}
          plan={plan}
          selected={selectedEvtTemplate}
          onSelect={setSelectedEvtTemplate}
          onCreate={handleCreateTemplate}
          eventTemplates={eventTemplates}
          onClose={props.onClose}
          onGoNext={actions.goForward}
        />
      ),
    },
    {
      key: 'page-2',
      render: actions =>
        selectedEvtTemplate && (
          <ModifyEventDetailsPage
            mode={mode}
            title={`${mode.type === 'create' ? 'Add' : 'Edit'} Event`}
            submitTitle={`Finish: ${mode.type === 'create' ? 'Create' : 'Edit'} Event`}
            plan={plan}
            selectedEvtTemplate={selectedEvtTemplate}
            eventTemplateTranslations={props.eventTemplateTranslations}
            onDeleteEvent={props.onRemoveEvent}
            venueTemplates={venueTemplates}
            staffTemplates={staffTemplates}
            onClose={props.onClose}
            onGoBack={actions.goBack}
            onSubmit={handleSubmit}
          />
        ),
    }
  );

  return (
    <div className="canvas-event-form">
      <BubbleForm pages={pages} startAtPage={mode.initialPage} />
    </div>
  );
}
